import { CONTENT_LIBRARY_PATH } from 'components/brand/constants';
import {
  useGetOnboardingStates,
  useMarkStepCompleted,
} from 'providers/ProgressiveOnboarding/queries';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const CONTENT_LIBRARY_BADGE_FEATURE_KEY = 'CONTENT_LIBRARY_BADGE_V1';
export const CONTENT_LIBRARY_BADGE_STEP_KEY = 'CONTENT_LIBRARY_VISITED';

const STORAGE_KEY = 'ps_content_library_new_badge_dismissed';

export const useContentLibraryNewBadge = (): boolean => {
  // localStorage gives us an instant render on page load (no flash while server request is in-flight)
  const [locallyDismissed, setLocallyDismissed] = useState(
    () => !!localStorage.getItem(STORAGE_KEY),
  );
  const { data: onboardingStates } = useGetOnboardingStates();
  const { mutate: markStepCompleted } = useMarkStepCompleted();
  const hasVisited = useRef(false);
  const { pathname } = useLocation();

  // Server state is the source of truth — sync to localStorage if dismissed on another device
  const serverDismissed =
    onboardingStates?.some(
      (state) =>
        state.featureKey === CONTENT_LIBRARY_BADGE_FEATURE_KEY &&
        state.states.some(
          (step) =>
            step.stepKey === CONTENT_LIBRARY_BADGE_STEP_KEY &&
            step.completedAt !== null,
        ),
    ) ?? false;

  useEffect(() => {
    if (serverDismissed && !locallyDismissed) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setLocallyDismissed(true);
    }
  }, [serverDismissed, locallyDismissed]);

  // Dismiss when navigating away after having visited Content Library
  useEffect(() => {
    if (locallyDismissed) return;

    if (pathname.startsWith(CONTENT_LIBRARY_PATH)) {
      hasVisited.current = true;
    } else if (hasVisited.current) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setLocallyDismissed(true);
      markStepCompleted({
        guideKey: CONTENT_LIBRARY_BADGE_FEATURE_KEY,
        stepKey: CONTENT_LIBRARY_BADGE_STEP_KEY,
      });
    }
  }, [pathname, locallyDismissed, markStepCompleted]);

  return !locallyDismissed && !serverDismissed;
};
